import { HttpErrorResponse } from '@angular/common/http';
import {
  InsufficientTokenBudgetError,
  InsufficientTokenBudgetErrorSchema,
} from '../models/insufficient-token-budget-error.model';
import { tokenTypeLabel } from './billing-page/billing-labels';

export { parseApiError } from '@tangible/shared';
export type { InsufficientTokenBudgetError };

/**
 * Returns true when the error is a 403 Forbidden response.
 * The verifyEmailInterceptor already handles these (toast or redirect),
 * so callers should skip showing their own error message.
 */
export function isPermissionError(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 403;
}

/**
 * Returns true when the error is a 402 Payment Required (insufficient token budget).
 * The paymentRequiredInterceptor already shows a toast, so callers should skip
 * showing their own error message.
 */
export function isPaymentRequiredError(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 402;
}

/**
 * Returns true when a global HTTP interceptor already surfaced the error.
 * Callers should skip showing their own error message.
 */
export function isInterceptorHandledError(error: unknown): boolean {
  return isPermissionError(error) || isPaymentRequiredError(error);
}

export function parseInsufficientTokenBudgetError(
  error: unknown,
): InsufficientTokenBudgetError | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== 402) {
    return null;
  }

  const parsed = InsufficientTokenBudgetErrorSchema.safeParse(error.error);
  return parsed.success ? parsed.data : null;
}

export function formatInsufficientTokenBudgetMessage(
  error: InsufficientTokenBudgetError,
): string {
  const label = tokenTypeLabel(error.token_type);
  const parts = [`No ${label} tokens remaining.`];

  if (error.on_demand_budget_exhausted) {
    parts.push(
      'On-demand billing is enabled, but your monthly budget has been fully used.',
    );
  } else if (error.on_demand_available) {
    parts.push(
      'Enable on-demand billing on the Billing page to continue using pay-as-you-go tokens.',
    );
  } else {
    parts.push('On-demand billing is not available for this action.');
  }

  return parts.join(' ');
}
